import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Ticket, MessageCircle, Loader2, X, CheckCircle2, Sparkles, Grid3x3, CreditCard, UserCheck, XCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { RAFFLE } from "@/lib/raffleData";
import FichaMap from "@/components/FichaMap";

const pad = (n) => String(n).padStart(4, "0");
const parseNum = (raw) => {
  const s = String(raw || "").trim();
  if (!s) return null;
  const n = parseInt(s, 10);
  if (Number.isNaN(n) || n < 0 || n > 9999) return null;
  return n;
};

export default function PublicPickFicha() {
  const [showMap, setShowMap] = useState(false);
  const [quick, setQuick] = useState("");
  const [modal, setModal] = useState({ open: false, ficha: "", buyer_name: "", buyer_phone: "", codigo_referido: "" });
  const [codigoState, setCodigoState] = useState({ checking: false, valid: null, seller_name: "" });
  const [busy, setBusy] = useState(false);
  const [wompiBusy, setWompiBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [confirmed, setConfirmed] = useState(null); // { ficha_number, buyer_name, buyer_phone, minutes, seller_name }

  const payWithWompi = async () => {
    if (!confirmed) return;
    setWompiBusy(true);
    try {
      const redirectUrl = `${window.location.origin}/pago/resultado`;
      const { data: intent } = await api.post("/wompi/checkout-intent", {
        ficha_number: confirmed.ficha_number,
        buyer_name: confirmed.buyer_name,
        buyer_phone: confirmed.buyer_phone,
        redirect_url: redirectUrl,
        codigo_referido: confirmed.codigo_referido || undefined,
      });
      // Build the Wompi Web Checkout form and submit
      const form = document.createElement("form");
      form.method = "GET";
      form.action = intent.checkout_url;
      const fields = {
        "public-key": intent.public_key,
        currency: intent.currency,
        "amount-in-cents": intent.amount_in_cents,
        reference: intent.reference,
        "signature:integrity": intent.integrity,
        "redirect-url": intent.redirect_url,
        "customer-data:full-name": confirmed.buyer_name || "",
        "customer-data:phone-number": confirmed.buyer_phone || "",
      };
      Object.entries(fields).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = String(v);
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setWompiBusy(false);
    }
  };

  const openModal = (ficha) => {
    setConfirmed(null);
    setCodigoState({ checking: false, valid: null, seller_name: "" });
    setModal({ open: true, ficha, buyer_name: "", buyer_phone: "", codigo_referido: "" });
  };

  const closeModal = () => setModal({ open: false, ficha: "", buyer_name: "", buyer_phone: "", codigo_referido: "" });

  // Live check of the referral code (debounced)
  const checkCodigo = async (raw) => {
    const code = (raw || "").trim().toUpperCase();
    if (!code) {
      setCodigoState({ checking: false, valid: null, seller_name: "" });
      return;
    }
    setCodigoState({ checking: true, valid: null, seller_name: "" });
    try {
      const { data } = await api.get(`/public/referido/${encodeURIComponent(code)}`);
      setCodigoState({ checking: false, valid: !!data.valid, seller_name: data.seller_name || "" });
    } catch {
      setCodigoState({ checking: false, valid: false, seller_name: "" });
    }
  };

  const handleQuick = (e) => {
    e.preventDefault();
    const n = parseNum(quick);
    if (n === null) {
      toast.error("Ingresa un número entre 0000 y 9999");
      return;
    }
    openModal(pad(n));
  };

  const handleCellClick = ({ ficha, state }) => {
    if (state === "sold") {
      toast(`Ficha #${ficha} ya fue vendida`, { description: "Elige otra ficha disponible" });
      return;
    }
    if (state === "reserved") {
      toast(`Ficha #${ficha} está reservada`, { description: "Elige otra ficha disponible" });
      return;
    }
    openModal(ficha);
  };

  // Debounced live-check of the referral code
  useEffect(() => {
    if (!modal.open) return;
    const t = setTimeout(() => checkCodigo(modal.codigo_referido), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.codigo_referido, modal.open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!modal.buyer_name.trim()) {
      toast.error("Ingresa tu nombre para apartar la ficha");
      return;
    }
    if (modal.codigo_referido.trim() && codigoState.valid === false) {
      toast.error("El código ingresado no es válido. Verifica con tu vendedor o deja el campo vacío.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/public/reserve", {
        ficha_number: modal.ficha,
        buyer_name: modal.buyer_name,
        buyer_phone: modal.buyer_phone,
        codigo_referido: modal.codigo_referido.trim().toUpperCase() || undefined,
      });
      setConfirmed(data);
      setRefreshKey((k) => k + 1);
      toast.success(`¡Ficha #${data.ficha_number} apartada por 30 min!`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const buildWa = () => {
    if (!confirmed) return "#";
    const msg =
      `Hola BETMAR 👋, aparté la ficha #${confirmed.ficha_number} a nombre de ${confirmed.buyer_name}` +
      `${confirmed.buyer_phone ? ` (tel: ${confirmed.buyer_phone})` : ""}.` +
      ` Quiero completar el pago de $${RAFFLE.fichaPrice} y confirmar mi participación en el Gran Proyecto.`;
    return `https://wa.me/${RAFFLE.whatsapp}?text=${encodeURIComponent(msg)}`;
  };

  const inputCls =
    "w-full bg-[#051A0F] border border-[#F0B91B]/30 rounded-xl px-4 py-3 text-[#FDFBF7] font-outfit placeholder:text-[#FDFBF7]/40 focus:outline-none focus:border-[#F0B91B] transition-colors";

  return (
    <section
      id="elegir-ficha"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="public-pick-section"
    >
      <div className="absolute inset-0 bg-[#0A2818]" />
      <div className="absolute top-0 right-0 w-[40%] h-full bg-gradient-to-l from-[#F0B91B]/10 to-transparent blur-2xl" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#F0B91B]/30 bg-[#F0B91B]/5 mb-6">
            <Sparkles className="w-4 h-4 text-[#F0B91B]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#F0B91B] font-semibold font-outfit">
              Elige tu número
            </span>
          </div>
          <h2 className="font-bebas text-5xl md:text-7xl text-[#FDFBF7] tracking-wider leading-tight">
            Tu número, <span className="gold-shimmer">tu suerte</span>
          </h2>
          <p className="mt-4 text-base md:text-lg text-[#FDFBF7]/75 font-outfit max-w-2xl mx-auto">
            Escoge la ficha que quieres <strong className="text-[#F0B91B]">antes de pagar</strong>. La apartamos por 30 minutos y te acompañamos por WhatsApp para completar tu compra.
          </p>
        </motion.div>

        {/* Quick input */}
        <motion.form
          onSubmit={handleQuick}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mx-auto max-w-xl rounded-3xl border-2 border-[#F0B91B]/40 bg-[#051A0F]/80 backdrop-blur-md p-5 md:p-6 shadow-[0_25px_60px_-25px_rgba(0,0,0,0.9)]"
          data-testid="public-quick-form"
        >
          <label className="block text-[10px] uppercase tracking-[0.25em] text-[#F0B91B] font-outfit font-bold mb-2">
            Ingresa tu número favorito (0000 – 9999)
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#F0B91B]" />
              <input
                value={quick}
                onChange={(e) => setQuick(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="Ej: 0777"
                inputMode="numeric"
                maxLength={4}
                className={inputCls + " pl-11 font-bebas text-2xl tracking-[0.4em]"}
                data-testid="public-quick-input"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 bg-[#F0B91B] hover:bg-[#FFD700] text-[#051A0F] px-6 py-3 rounded-xl font-bebas text-lg md:text-xl tracking-wider shadow-[0_15px_40px_-10px_rgba(240,185,27,0.7)] hover:-translate-y-0.5 transition-all"
              data-testid="public-quick-submit"
            >
              Apartar ficha
            </button>
          </div>
          <button
            type="button"
            onClick={() => setShowMap((s) => !s)}
            className="mt-4 inline-flex items-center gap-2 text-sm text-[#F0B91B] hover:text-[#FFD700] font-outfit underline underline-offset-2"
            data-testid="public-toggle-map"
          >
            <Grid3x3 className="w-4 h-4" />
            {showMap ? "Ocultar mapa completo" : "Ver mapa completo con disponibilidad"}
          </button>
        </motion.form>

        {/* Full map (collapsible) */}
        {showMap && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            transition={{ duration: 0.4 }}
            className="mt-8"
          >
            <FichaMap publicEndpoint refreshKey={refreshKey} onCellClick={handleCellClick} />
          </motion.div>
        )}
      </div>

      {/* Modal */}
      {modal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="public-pick-modal">
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={closeModal} />
          <div className="relative w-full max-w-md rounded-3xl border-2 border-[#F0B91B]/40 bg-[#0A2818] p-6 md:p-8 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.95)]">
            <button onClick={closeModal} className="absolute top-4 right-4 text-[#FDFBF7]/50 hover:text-[#E63946]" data-testid="public-modal-close">
              <X className="w-5 h-5" />
            </button>

            {!confirmed ? (
              <>
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F0B91B]/15 border-2 border-[#F0B91B]/40 mb-3">
                    <Ticket className="w-8 h-8 text-[#F0B91B]" />
                  </div>
                  <h3 className="font-bebas text-3xl text-[#FDFBF7] tracking-wider">
                    Aparta la ficha <span className="gold-shimmer">#{modal.ficha}</span>
                  </h3>
                  <p className="text-xs text-[#FDFBF7]/60 font-outfit mt-1">
                    La reservamos por 30 minutos mientras coordinas el pago
                  </p>
                </div>

                <form onSubmit={submit} className="space-y-4" data-testid="public-modal-form">
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-[#F0B91B] font-outfit font-bold mb-1.5">Tu nombre completo *</label>
                    <input
                      value={modal.buyer_name}
                      onChange={(e) => setModal({ ...modal, buyer_name: e.target.value })}
                      placeholder="Ej: María Rodríguez"
                      className={inputCls}
                      autoFocus
                      data-testid="public-modal-name"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-[#F0B91B] font-outfit font-bold mb-1.5">Tu WhatsApp (opcional)</label>
                    <input
                      value={modal.buyer_phone}
                      onChange={(e) => setModal({ ...modal, buyer_phone: e.target.value })}
                      placeholder="Ej: 300 123 4567"
                      inputMode="tel"
                      className={inputCls}
                      data-testid="public-modal-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-[#F0B91B] font-outfit font-bold mb-1.5">¿Tienes un código de tu vendedor?</label>
                    <div className="relative">
                      <input
                        value={modal.codigo_referido}
                        onChange={(e) => setModal({ ...modal, codigo_referido: e.target.value.toUpperCase() })}
                        placeholder="Ingrésalo aquí (opcional)"
                        className={`${inputCls} uppercase tracking-widest pr-11 ${
                          modal.codigo_referido.trim() && codigoState.valid === false
                            ? "border-[#E63946]/70 focus:border-[#E63946]"
                            : modal.codigo_referido.trim() && codigoState.valid === true
                            ? "border-[#7FD1AE]/70 focus:border-[#7FD1AE]"
                            : ""
                        }`}
                        maxLength={20}
                        data-testid="public-modal-codigo"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {codigoState.checking ? (
                          <Loader2 className="w-4 h-4 text-[#F0B91B] animate-spin" />
                        ) : modal.codigo_referido.trim() && codigoState.valid === true ? (
                          <UserCheck className="w-5 h-5 text-[#7FD1AE]" />
                        ) : modal.codigo_referido.trim() && codigoState.valid === false ? (
                          <XCircle className="w-5 h-5 text-[#E63946]" />
                        ) : null}
                      </div>
                    </div>
                    {modal.codigo_referido.trim() && codigoState.valid === true && (
                      <p className="text-[11px] font-outfit text-[#7FD1AE] mt-1.5 flex items-center gap-1" data-testid="public-modal-codigo-ok">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Vendedor asignado: <strong>{codigoState.seller_name}</strong>
                      </p>
                    )}
                    {modal.codigo_referido.trim() && codigoState.valid === false && (
                      <p className="text-[11px] font-outfit text-[#E63946] mt-1.5" data-testid="public-modal-codigo-error">
                        El código ingresado no es válido. Verifica con tu vendedor o deja el campo vacío.
                      </p>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={busy || (modal.codigo_referido.trim() && codigoState.valid === false)}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#F0B91B] hover:bg-[#FFD700] disabled:opacity-50 disabled:cursor-not-allowed text-[#051A0F] py-3.5 rounded-xl font-bebas text-lg md:text-xl tracking-wider shadow-[0_15px_40px_-10px_rgba(240,185,27,0.7)] transition-all"
                    data-testid="public-modal-submit"
                  >
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : `Apartar ficha #${modal.ficha}`}
                  </button>
                </form>
              </>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#7FD1AE]/15 border-2 border-[#7FD1AE]/40 mb-3">
                    <CheckCircle2 className="w-8 h-8 text-[#7FD1AE]" />
                  </div>
                  <h3 className="font-bebas text-3xl text-[#FDFBF7] tracking-wider">
                    ¡Ficha <span className="gold-shimmer">#{confirmed.ficha_number}</span> apartada!
                  </h3>
                  <p className="text-sm text-[#FDFBF7]/70 font-outfit mt-2">
                    Tenemos tu ficha reservada por <strong className="text-[#F0B91B]">{confirmed.minutes} minutos</strong>.
                    Continúa por WhatsApp para completar el pago de <strong className="text-[#F0B91B]">${RAFFLE.fichaPrice}</strong>.
                  </p>
                </div>
                <div className="rounded-xl bg-[#051A0F] border border-[#F0B91B]/20 p-4 mb-4 text-sm font-outfit text-[#FDFBF7]/85 space-y-1" data-testid="public-modal-confirmed">
                  <div><span className="text-[#F0B91B]">Ficha:</span> #{confirmed.ficha_number}</div>
                  <div><span className="text-[#F0B91B]">A nombre de:</span> {confirmed.buyer_name}</div>
                  {confirmed.buyer_phone && (
                    <div><span className="text-[#F0B91B]">Contacto:</span> {confirmed.buyer_phone}</div>
                  )}
                  {confirmed.codigo_referido && confirmed.seller_name && (
                    <div className="pt-1 border-t border-[#F0B91B]/15 mt-1.5" data-testid="public-modal-confirmed-seller">
                      <span className="text-[#F0B91B]">Vendedor asignado:</span> <strong>{confirmed.seller_name}</strong> (<span className="tracking-widest">{confirmed.codigo_referido}</span>)
                    </div>
                  )}
                </div>
                <div className="space-y-2.5">
                  <button
                    onClick={payWithWompi}
                    disabled={wompiBusy}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#F0B91B] hover:bg-[#FFD700] disabled:opacity-60 text-[#051A0F] py-3.5 rounded-xl font-bebas text-lg md:text-xl tracking-wider shadow-[0_15px_40px_-10px_rgba(240,185,27,0.75)] transition-all hover:-translate-y-0.5"
                    data-testid="public-modal-wompi"
                  >
                    {wompiBusy ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5" />
                        Pagar en línea con Wompi
                      </>
                    )}
                  </button>
                  <div className="flex items-center gap-3 my-1">
                    <div className="flex-1 h-px bg-[#F0B91B]/20" />
                    <span className="text-[10px] uppercase tracking-widest text-[#FDFBF7]/40 font-outfit">o</span>
                    <div className="flex-1 h-px bg-[#F0B91B]/20" />
                  </div>
                  <a
                    href={buildWa()}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FB855] text-white py-3.5 rounded-xl font-bebas text-lg md:text-xl tracking-wider shadow-[0_15px_40px_-10px_rgba(37,211,102,0.7)] transition-all hover:-translate-y-0.5"
                    data-testid="public-modal-wa"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Continuar por WhatsApp
                  </a>
                  <p className="text-[10px] uppercase tracking-widest text-center text-[#FDFBF7]/40 font-outfit pt-1">
                    Pago seguro procesado por Wompi (Bancolombia)
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="w-full mt-2 text-xs uppercase tracking-widest text-[#FDFBF7]/50 hover:text-[#FDFBF7]/80 py-2 font-outfit"
                >
                  Cerrar
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
